import React from 'react';

// Enums matching the backend
export enum ScheduleFrequency {
  ONE_TIME = 'ONE_TIME',
  WEEKLY = 'WEEKLY',
  BI_WEEKLY = 'BI_WEEKLY',
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  ANNUALLY = 'ANNUALLY',
}

export enum BiWeeklyPattern {
  EVERY_2_WEEKS = 'EVERY_2_WEEKS',
  FIRST_AND_THIRD = 'FIRST_AND_THIRD',
  SECOND_AND_FOURTH = 'SECOND_AND_FOURTH',
}

export enum MonthlyPattern {
  SPECIFIC_DAY = 'SPECIFIC_DAY',
  LAST_DAY = 'LAST_DAY',
  NTH_WEEKDAY = 'NTH_WEEKDAY',
}

export enum QuarterlyPattern {
  START_OF_QUARTER = 'START_OF_QUARTER',
  END_OF_QUARTER = 'END_OF_QUARTER',
  NTH_DAY_OF_FIRST_MONTH = 'NTH_DAY_OF_FIRST_MONTH',
  NTH_DAY_OF_LAST_MONTH = 'NTH_DAY_OF_LAST_MONTH',
}

export enum AnnualPattern {
  SPECIFIC_DATE = 'SPECIFIC_DATE',
  NTH_WEEKDAY_OF_MONTH = 'NTH_WEEKDAY_OF_MONTH',
}

export enum Weekday {
  SUNDAY = 0,
  MONDAY = 1,
  TUESDAY = 2,
  WEDNESDAY = 3,
  THURSDAY = 4,
  FRIDAY = 5,
  SATURDAY = 6,
}

export enum NthOccurrence {
  FIRST = 1,
  SECOND = 2,
  THIRD = 3,
  FOURTH = 4,
  LAST = -1,
}

interface RecurrenceRule {
  frequency: ScheduleFrequency;
  
  // Weekly
  weeklyDay?: Weekday;
  
  // Bi-Weekly
  biWeeklyPattern?: BiWeeklyPattern;
  biWeeklyDay?: Weekday;
  
  // Monthly
  monthlyPattern?: MonthlyPattern;
  monthlyDay?: number;
  monthlyNthOccurrence?: NthOccurrence;
  monthlyWeekday?: Weekday;
  
  // Quarterly
  quarterlyPattern?: QuarterlyPattern;
  quarterlyDay?: number;
  
  // Annually
  annualPattern?: AnnualPattern;
  annualMonth?: number;
  annualDay?: number;
  annualNthOccurrence?: NthOccurrence;
  annualWeekday?: Weekday;
  
  // Common
  endDate?: string;
  maxOccurrences?: number;
  timezone?: string;
}

interface TaskSchedule {
  frequency: string;
  dueDate: string;
  periodStart: string;
  periodEnd: string;
  recurrenceRule?: RecurrenceRule;
  hasEndCondition?: boolean;
}

interface TaskScheduleFormProps {
  schedule: TaskSchedule;
  onChange: (schedule: TaskSchedule) => void;
  errors?: { [key: string]: string };
}

const weekdayOptions = [
  { value: Weekday.SUNDAY, label: 'Sunday' },
  { value: Weekday.MONDAY, label: 'Monday' },
  { value: Weekday.TUESDAY, label: 'Tuesday' },
  { value: Weekday.WEDNESDAY, label: 'Wednesday' },
  { value: Weekday.THURSDAY, label: 'Thursday' },
  { value: Weekday.FRIDAY, label: 'Friday' },
  { value: Weekday.SATURDAY, label: 'Saturday' },
];

const nthOccurrenceOptions = [
  { value: NthOccurrence.FIRST, label: '1st' },
  { value: NthOccurrence.SECOND, label: '2nd' },
  { value: NthOccurrence.THIRD, label: '3rd' },
  { value: NthOccurrence.FOURTH, label: '4th' },
  { value: NthOccurrence.LAST, label: 'Last' },
];

const monthOptions = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

export const TaskScheduleForm: React.FC<TaskScheduleFormProps> = ({ schedule, onChange, errors = {} }) => {
  const updateSchedule = (updates: Partial<TaskSchedule>) => {
    onChange({ ...schedule, ...updates });
  };

  const updateRecurrenceRule = (updates: Partial<RecurrenceRule>) => {
    const currentRule = schedule.recurrenceRule || {};

    // Filter out undefined values from updates
    const filteredUpdates = Object.entries(updates).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    const newRule = {
      ...currentRule,
      frequency: schedule.frequency as ScheduleFrequency,
      ...filteredUpdates
    };

    // Remove fields that were explicitly set to undefined
    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined && key in newRule) {
        delete newRule[key];
      }
    });

    updateSchedule({ recurrenceRule: newRule });
  };

  const handleFrequencyChange = (frequency: string) => {
    // Create a new recurrence rule with the new frequency
    // Preserve common fields like endDate and maxOccurrences if they exist
    const currentRule = schedule.recurrenceRule || {};
    const newRule: RecurrenceRule = {
      frequency: frequency as ScheduleFrequency,
      // Preserve end condition fields if they exist
      ...(currentRule.endDate && { endDate: currentRule.endDate }),
      ...(currentRule.maxOccurrences && { maxOccurrences: currentRule.maxOccurrences }),
      ...(currentRule.timezone && { timezone: currentRule.timezone })
    };

    // Clear due date for recurring tasks, clear period dates for one-time tasks
    const updates: Partial<TaskSchedule> = {
      frequency,
      recurrenceRule: newRule,
      hasEndCondition: schedule.hasEndCondition || false
    };

    if (frequency === 'ONE_TIME') {
      updates.periodStart = '';
      updates.periodEnd = '';
    } else {
      updates.dueDate = '';
    }

    updateSchedule(updates);
  };

  const handleEndConditionChange = (hasEndCondition: boolean) => {
    const updates: Partial<TaskSchedule> = { hasEndCondition };
    if (!hasEndCondition) {
      // Clear end condition fields when unchecked
      updates.periodEnd = '';

      if (schedule.recurrenceRule) {
        // Create a new recurrence rule without endDate and maxOccurrences
        const currentRule = schedule.recurrenceRule;
        const { endDate, maxOccurrences, ...cleanRule } = currentRule;

        updateSchedule({
          ...updates,
          recurrenceRule: {
            ...cleanRule,
            frequency: schedule.frequency as ScheduleFrequency
          }
        });
        return;
      }
    }
    updateSchedule(updates);
  };

  const renderFrequencySpecificFields = () => {
    const frequency = schedule.frequency as ScheduleFrequency;
    
    switch (frequency) {
      case ScheduleFrequency.ONE_TIME:
        return null;

      case ScheduleFrequency.WEEKLY:
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Day of Week *
            </label>
            <select
              value={schedule.recurrenceRule?.weeklyDay ?? ''}
              onChange={(e) => updateRecurrenceRule({ weeklyDay: parseInt(e.target.value) as Weekday })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            >
              <option value="">Select day</option>
              {weekdayOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.weeklyDay && <p className="mt-1 text-sm text-red-600">{errors.weeklyDay}</p>}
          </div>
        );

      case ScheduleFrequency.BI_WEEKLY:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bi-Weekly Pattern *
              </label>
              <select
                value={schedule.recurrenceRule?.biWeeklyPattern ?? ''}
                onChange={(e) => updateRecurrenceRule({ biWeeklyPattern: e.target.value as BiWeeklyPattern })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              >
                <option value="">Select pattern</option>
                <option value={BiWeeklyPattern.EVERY_2_WEEKS}>Every 2 weeks</option>
                <option value={BiWeeklyPattern.FIRST_AND_THIRD}>1st and 3rd week of month</option>
                <option value={BiWeeklyPattern.SECOND_AND_FOURTH}>2nd and 4th week of month</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Day of Week *
              </label>
              <select
                value={schedule.recurrenceRule?.biWeeklyDay ?? ''}
                onChange={(e) => updateRecurrenceRule({ biWeeklyDay: parseInt(e.target.value) as Weekday })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              >
                <option value="">Select day</option>
                {weekdayOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      case ScheduleFrequency.MONTHLY:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Monthly Pattern *
              </label>
              <select
                value={schedule.recurrenceRule?.monthlyPattern ?? ''}
                onChange={(e) => updateRecurrenceRule({ monthlyPattern: e.target.value as MonthlyPattern })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              >
                <option value="">Select pattern</option>
                <option value={MonthlyPattern.SPECIFIC_DAY}>Specific day of month</option>
                <option value={MonthlyPattern.LAST_DAY}>Last day of month</option>
                <option value={MonthlyPattern.NTH_WEEKDAY}>Nth weekday of month</option>
              </select>
            </div>
            
            {schedule.recurrenceRule?.monthlyPattern === MonthlyPattern.SPECIFIC_DAY && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Day of Month (1-31) *
                </label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={schedule.recurrenceRule?.monthlyDay ?? ''}
                  onChange={(e) => updateRecurrenceRule({ monthlyDay: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
            )}
            
            {schedule.recurrenceRule?.monthlyPattern === MonthlyPattern.NTH_WEEKDAY && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Occurrence *
                  </label>
                  <select
                    value={schedule.recurrenceRule?.monthlyNthOccurrence ?? ''}
                    onChange={(e) => updateRecurrenceRule({ monthlyNthOccurrence: parseInt(e.target.value) as NthOccurrence })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select</option>
                    {nthOccurrenceOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Weekday *
                  </label>
                  <select
                    value={schedule.recurrenceRule?.monthlyWeekday ?? ''}
                    onChange={(e) => updateRecurrenceRule({ monthlyWeekday: parseInt(e.target.value) as Weekday })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select day</option>
                    {weekdayOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        );

      case ScheduleFrequency.QUARTERLY:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quarterly Pattern *
              </label>
              <select
                value={schedule.recurrenceRule?.quarterlyPattern ?? ''}
                onChange={(e) => updateRecurrenceRule({ quarterlyPattern: e.target.value as QuarterlyPattern })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              >
                <option value="">Select pattern</option>
                <option value={QuarterlyPattern.START_OF_QUARTER}>Start of quarter</option>
                <option value={QuarterlyPattern.END_OF_QUARTER}>End of quarter</option>
                <option value={QuarterlyPattern.NTH_DAY_OF_FIRST_MONTH}>Nth day of first month</option>
                <option value={QuarterlyPattern.NTH_DAY_OF_LAST_MONTH}>Nth day of last month</option>
              </select>
            </div>

            {(schedule.recurrenceRule?.quarterlyPattern === QuarterlyPattern.NTH_DAY_OF_FIRST_MONTH ||
              schedule.recurrenceRule?.quarterlyPattern === QuarterlyPattern.NTH_DAY_OF_LAST_MONTH) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Day of Month (1-31) *
                </label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={schedule.recurrenceRule?.quarterlyDay ?? ''}
                  onChange={(e) => updateRecurrenceRule({ quarterlyDay: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
            )}
          </div>
        );

      case ScheduleFrequency.ANNUALLY:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Annual Pattern *
              </label>
              <select
                value={schedule.recurrenceRule?.annualPattern ?? ''}
                onChange={(e) => updateRecurrenceRule({ annualPattern: e.target.value as AnnualPattern })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              >
                <option value="">Select pattern</option>
                <option value={AnnualPattern.SPECIFIC_DATE}>Specific date each year</option>
                <option value={AnnualPattern.NTH_WEEKDAY_OF_MONTH}>Nth weekday of month</option>
              </select>
            </div>

            {schedule.recurrenceRule?.annualPattern === AnnualPattern.SPECIFIC_DATE && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Month *
                  </label>
                  <select
                    value={schedule.recurrenceRule?.annualMonth ?? ''}
                    onChange={(e) => updateRecurrenceRule({ annualMonth: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select month</option>
                    {monthOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Day *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={schedule.recurrenceRule?.annualDay ?? ''}
                    onChange={(e) => updateRecurrenceRule({ annualDay: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
            )}

            {schedule.recurrenceRule?.annualPattern === AnnualPattern.NTH_WEEKDAY_OF_MONTH && (
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Occurrence *
                  </label>
                  <select
                    value={schedule.recurrenceRule?.annualNthOccurrence ?? ''}
                    onChange={(e) => updateRecurrenceRule({ annualNthOccurrence: parseInt(e.target.value) as NthOccurrence })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select</option>
                    {nthOccurrenceOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Weekday *
                  </label>
                  <select
                    value={schedule.recurrenceRule?.annualWeekday ?? ''}
                    onChange={(e) => updateRecurrenceRule({ annualWeekday: parseInt(e.target.value) as Weekday })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select day</option>
                    {weekdayOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Month *
                  </label>
                  <select
                    value={schedule.recurrenceRule?.annualMonth ?? ''}
                    onChange={(e) => updateRecurrenceRule({ annualMonth: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select month</option>
                    {monthOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-4">Schedule</h3>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Frequency *
            </label>
            <select
              value={schedule.frequency}
              onChange={(e) => handleFrequencyChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            >
              <option value="ONE_TIME">One Time</option>
              <option value="WEEKLY">Weekly</option>
              <option value="BI_WEEKLY">Bi-Weekly</option>
              <option value="MONTHLY">Monthly</option>
              <option value="QUARTERLY">Quarterly</option>
              <option value="ANNUALLY">Annually</option>
            </select>
            {errors.frequency && <p className="mt-1 text-sm text-red-600">{errors.frequency}</p>}
          </div>

          {/* Due Date - Only for One Time tasks */}
          {schedule.frequency === 'ONE_TIME' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Due Date *
              </label>
              <input
                type="date"
                value={schedule.dueDate}
                onChange={(e) => updateSchedule({ dueDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
              {errors.dueDate && <p className="mt-1 text-sm text-red-600">{errors.dueDate}</p>}
            </div>
          )}
        </div>

        {/* Frequency-specific fields */}
        {renderFrequencySpecificFields()}

        {/* Period fields for non-one-time tasks */}
        {schedule.frequency !== 'ONE_TIME' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Period Start
              </label>
              <input
                type="date"
                value={schedule.periodStart}
                onChange={(e) => updateSchedule({ periodStart: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* End Condition Checkbox */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="hasEndCondition"
                checked={schedule.hasEndCondition || false}
                onChange={(e) => handleEndConditionChange(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="hasEndCondition" className="text-sm font-medium text-gray-700">
                Set end condition for this recurring task
              </label>
            </div>

            {/* End Condition Fields - Only show when checkbox is checked */}
            {schedule.hasEndCondition && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6 border-l-2 border-gray-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={schedule.periodEnd}
                    onChange={(e) => updateSchedule({ periodEnd: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Tasks will stop recurring after this date
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Occurrences
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={schedule.recurrenceRule?.maxOccurrences || ''}
                    onChange={(e) => updateRecurrenceRule({ maxOccurrences: e.target.value ? parseInt(e.target.value) : undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., 10"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Maximum number of task instances to create
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
